import { useState, type ReactNode } from 'react';

import { useLocation } from 'react-router-dom';

import type { CurrentUser } from '@/api/schema';
import { cx } from '@/lib/cx';
import { usePersistentState } from '@/lib/usePersistentState';

import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import styles from './AppShell.module.css';

/**
 * The frame every signed-in screen sits inside.
 *
 * Holds no feature logic: navigation, breadcrumb, user menu, and a slot.
 */
export function AppShell({ user, children }: { user: CurrentUser; children: ReactNode }) {
  const location = useLocation();
  const [mini, setMini] = usePersistentState('flyst.nav.mini', false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // The overlay must not survive a navigation, or the new screen opens underneath it.
  //
  // Adjusted during render rather than in an effect: this is React's documented way
  // to reset state when something it depends on changes, and it avoids the extra
  // commit (and the flash of the old overlay) that an effect would cause.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setMobileOpen(false);
  }

  return (
    <div className={styles.shell}>
      <Sidebar
        user={user}
        mini={mini}
        onToggleMini={() => {
          setMini((current) => !current);
        }}
        mobileOpen={mobileOpen}
        onNavigate={() => {
          setMobileOpen(false);
        }}
      />

      {mobileOpen ? (
        <div
          className={styles.scrim}
          onClick={() => {
            setMobileOpen(false);
          }}
        />
      ) : null}

      <div className={cx(styles.main)}>
        <Topbar
          user={user}
          onOpenNav={() => {
            setMobileOpen(true);
          }}
        />
        {/* Only this scrolls, so the navigation and breadcrumb stay put. */}
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
